import React, { useContext, useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { usePermission } from '../../context/PermissionContext';
import { 
  Home, 
  RefreshCw, 
  FileText, 
  BarChart2,
  Settings,
  Shield,
  CreditCard
} from 'lucide-react';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { user } = useContext(AuthContext);
  const { hasPermission } = usePermission();
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  
  // Dark mode değişikliklerini dinle
  useEffect(() => {
    const handleDarkModeChange = () => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    };
    
    // Storage event handler'ı - ayrı fonksiyon olarak tanımlandı
    const handleStorageChange = (e) => {
      if (e.key === 'darkMode') {
        setDarkMode(e.newValue === 'true');
      }
    };
    
    // Custom event dinle
    window.addEventListener('darkModeChange', handleDarkModeChange);
    
    // Storage event dinle (farklı sekmelerde senkronizasyon için)
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('darkModeChange', handleDarkModeChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // NavLink için sınıf isimleri
  const normalClass = "text-gray-700 dark:text-gray-300";
  const activeClass = "bg-primary-100 text-primary-700 dark:bg-gray-700 dark:text-white";
  
  // İkon boyutu - Sidebar kapalıyken daha büyük
  const iconSize = isOpen ? 20 : 24;
  
  // Permission check helper function
  const checkPermission = (requiredPermissions = [], anyPermission = []) => {
    if (!user) return false;
    
    // Check "any permission" logic (OR)
    if (anyPermission.length > 0) {
      return anyPermission.some(perm => hasPermission(perm));
    }
    
    // Check "all required permissions" logic (AND)
    if (requiredPermissions.length > 0) {
      return requiredPermissions.every(perm => hasPermission(perm));
    }
    
    return true;
  };
  
  return (
    <aside 
      className={`fixed inset-y-0 left-0 z-10 pt-16 transition-all duration-300 transform bg-white dark:bg-gray-800 shadow-md ${
        isOpen ? 'w-64' : 'w-20'
      } md:translate-x-0 flex flex-col`}
    >
      <div className="h-full flex flex-col overflow-y-auto">
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {/* Ana Sayfa */}
            <li>
              <NavLink 
                to="/dashboard" 
                className={({ isActive }) => 
                  `flex items-center justify-${isOpen ? 'start' : 'center'} px-4 py-2 rounded-lg ${isActive ? activeClass : normalClass}`
                }
              >
                <Home size={iconSize} className={isOpen ? "mr-3" : ""} />
                <span className={isOpen ? 'block' : 'hidden'}>Ana Sayfa</span>
              </NavLink>
            </li>
            
            {/* İade İşlemleri */}
            <li>
              <NavLink 
                to="/refunds" 
                className={({ isActive }) => 
                  `flex items-center justify-${isOpen ? 'start' : 'center'} px-4 py-2 rounded-lg ${isActive ? activeClass : normalClass}`
                }
              >
                <RefreshCw size={iconSize} className={isOpen ? "mr-3" : ""} />
                <span className={isOpen ? 'block' : 'hidden'}>İade İşlemleri</span>
              </NavLink>
            </li>
            
            {/* Raporlar - Raporları görüntüleme izni olanlar */}
            {checkPermission(['view_reports']) && (
              <li>
                <NavLink 
                  to="/reports" 
                  className={({ isActive }) => 
                    `flex items-center justify-${isOpen ? 'start' : 'center'} px-4 py-2 rounded-lg ${isActive ? activeClass : normalClass}`
                  }
                >
                  <BarChart2 size={iconSize} className={isOpen ? "mr-3" : ""} />
                  <span className={isOpen ? 'block' : 'hidden'}>Raporlar</span>
                </NavLink>
              </li>
            )}

            {/* Sistem Logları - Sadece log görüntüleme yetkisi yeterli */}
            {checkPermission(['view_logs']) && (
              <li>
                <NavLink 
                  to="/logs" 
                  className={({ isActive }) => 
                    `flex items-center justify-${isOpen ? 'start' : 'center'} px-4 py-2 rounded-lg ${isActive ? activeClass : normalClass}`
                  }
                >
                  <FileText size={iconSize} className={isOpen ? "mr-3" : ""} />
                  <span className={isOpen ? 'block' : 'hidden'}>Sistem Logları</span>
                </NavLink>
              </li>
            )}
            
            {/* Yönetim Paneli - Farklı yönetim izinlerinden herhangi birine sahip olanlar */}
            {checkPermission([], [
              'view_admin_panel',
              'manage_users', 
              'manage_roles', 
              'manage_statuses', 
              'manage_payment_methods', 
              'manage_reasons'
            ]) && (
              <li>
                <NavLink 
                  to="/admin" 
                  className={({ isActive }) => 
                    `flex items-center justify-${isOpen ? 'start' : 'center'} px-4 py-2 rounded-lg ${isActive ? activeClass : normalClass}`
                  }
                >
                  <Shield size={iconSize} className={isOpen ? "mr-3" : ""} />
                  <span className={isOpen ? 'block' : 'hidden'}>Yönetim Paneli</span>
                </NavLink>
              </li>
            )}
            
            {/* Ayarlar - Sadece ayarlara erişim izni olanlar */}
            {checkPermission(['access_settings_module']) && (
              <li>
                <NavLink 
                  to="/settings" 
                  className={({ isActive }) => 
                    `flex items-center justify-${isOpen ? 'start' : 'center'} px-4 py-2 rounded-lg ${isActive ? activeClass : normalClass}`
                  }
                >
                  <Settings size={iconSize} className={isOpen ? "mr-3" : ""} />
                  <span className={isOpen ? 'block' : 'hidden'}>Ayarlar</span>
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
      </div>
      
      {/* Sidebar toggle butonu */}
      <button 
        className="absolute right-0 top-20 -mr-3 p-1 bg-white dark:bg-gray-700 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none"
        onClick={toggleSidebar}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-4 w-4 text-gray-700 dark:text-gray-300 transform transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`} 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
    </aside>
  );
};

export default Sidebar;